import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type GroupGoalStatus = 'pending' | 'achieved' | 'not_achieved';

export type GroupGoalRow = {
  id: string;
  group_id: string;
  title: string;
  description?: string | null;
  due_date: string;
  status: GroupGoalStatus;
  created_at: string;
  groups?: { name?: string | null } | Array<{ name?: string | null }> | null;
};

export type GroupGoalSummary = {
  groupId: string;
  groupName: string;
  total: number;
  achieved: number;
  notAchieved: number;
  pending: number;
  achievedPct: number;
  notAchievedPct: number;
};

export type GroupGoalsOverview = {
  goals: GroupGoalRow[];
  overall: {
    total: number;
    achieved: number;
    notAchieved: number;
    pending: number;
    achievedPct: number;
    notAchievedPct: number;
  };
  byGroup: GroupGoalSummary[];
};

const isOverdue = (goal: GroupGoalRow): boolean => {
  if (goal.status === 'achieved') return false;
  const dueDate = new Date(goal.due_date);
  if (Number.isNaN(dueDate.getTime())) return false;
  const today = new Date();
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

const pct = (count: number, total: number) => (total > 0 ? Math.round((count / total) * 100) : 0);

const getGroupName = (goal: GroupGoalRow): string => {
  if (!goal.groups) return 'Unknown Group';
  if (Array.isArray(goal.groups)) {
    return goal.groups[0]?.name?.trim() || 'Unknown Group';
  }
  return goal.groups.name?.trim() || 'Unknown Group';
};

async function fetchGroupGoalsOverview(): Promise<GroupGoalsOverview> {
  const { data, error } = await supabase
    .from('group_goals')
    .select('id, group_id, title, description, due_date, status, created_at, groups(name)')
    .order('due_date', { ascending: true });

  if (error) throw error;

  const goals = (data ?? []) as GroupGoalRow[];

  let achieved = 0;
  let notAchieved = 0;
  let pending = 0;

  const byGroupMap = new Map<string, GroupGoalSummary>();

  for (const goal of goals) {
    const groupName = getGroupName(goal);
    const groupKey = goal.group_id;

    if (!byGroupMap.has(groupKey)) {
      byGroupMap.set(groupKey, {
        groupId: groupKey,
        groupName,
        total: 0,
        achieved: 0,
        notAchieved: 0,
        pending: 0,
        achievedPct: 0,
        notAchievedPct: 0,
      });
    }

    const group = byGroupMap.get(groupKey)!;
    group.total += 1;

    if (goal.status === 'achieved') {
      achieved += 1;
      group.achieved += 1;
    } else if (goal.status === 'not_achieved' || isOverdue(goal)) {
      notAchieved += 1;
      group.notAchieved += 1;
    } else {
      pending += 1;
      group.pending += 1;
    }
  }

  const total = goals.length;
  const byGroup = Array.from(byGroupMap.values())
    .map((group) => ({
      ...group,
      achievedPct: pct(group.achieved, group.total),
      notAchievedPct: pct(group.notAchieved, group.total),
    }))
    .sort((a, b) => b.total - a.total || a.groupName.localeCompare(b.groupName));

  return {
    goals,
    overall: {
      total,
      achieved,
      notAchieved,
      pending,
      achievedPct: pct(achieved, total),
      notAchievedPct: pct(notAchieved, total),
    },
    byGroup,
  };
}

export function useGroupGoalsOverview() {
  return useQuery({
    queryKey: ['groups', 'goals', 'overview'],
    queryFn: fetchGroupGoalsOverview,
  });
}
